import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import FadeUp from '../components/FadeUp';
import SEO from '../components/SEO';
import { AppStoreButton, PlayStoreButton, SmartQRCode } from '../components/StoreBadges';
import { Calendar, Video, Play, Dumbbell, Apple, MessageCircle, ChevronRight, Award, FlaskConical, TrendingUp } from 'lucide-react';

export default function Landing() {
  const { t, lang } = useLanguage();

  const seoDesc = lang === 'es'
    ? 'Coaching de fuerza con 14+ años de experiencia. Reserva sesiones, videoconsultas, programas de entrenamiento, nutrición y chat directo con Anyelo.'
    : 'Strength coaching with 14+ years of experience. Book sessions, video consultations, training programs, nutrition plans and direct chat with Anyelo.';


  const features = [
    { icon: Calendar, title: t('feature_1_title'), desc: t('feature_1_desc') },
    { icon: Video, title: t('feature_2_title'), desc: t('feature_2_desc') },
    { icon: Play, title: t('feature_3_title'), desc: t('feature_3_desc') },
    { icon: Dumbbell, title: t('feature_4_title'), desc: t('feature_4_desc') },
    { icon: Apple, title: t('feature_5_title'), desc: t('feature_5_desc') },
    { icon: MessageCircle, title: t('feature_6_title'), desc: t('feature_6_desc') },
  ];

  const steps = [
    { num: '01', title: t('how_step_1_title'), desc: t('how_step_1_desc') },
    { num: '02', title: t('how_step_2_title'), desc: t('how_step_2_desc') },
    { num: '03', title: t('how_step_3_title'), desc: t('how_step_3_desc') },
  ];

  const bioStats = [
    { icon: Award, text: t('bio_experience') },
    { icon: FlaskConical, text: t('bio_method') },
    { icon: TrendingUp, text: t('bio_results') },
  ];

  return (
    <main>
      <SEO
        title={lang === 'es' ? 'Inicio' : 'Home'}
        description={seoDesc}
        path="/"
      />
      {/* ===== HERO ===== */}
      <section data-testid="landing-hero" className="relative overflow-hidden" style={{ backgroundColor: '#030303' }}>
        {/* Decorative gradients */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-0 left-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px]" style={{ background: 'radial-gradient(60% 60% at 20% 10%, rgba(200,255,0,0.08), transparent 55%)' }} />
          <div className="absolute top-0 right-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px]" style={{ background: 'radial-gradient(55% 55% at 80% 0%, rgba(255,255,255,0.04), transparent 60%)' }} />
        </div>

        <div className="relative mx-auto max-w-[1120px] px-4 sm:px-6 lg:px-8 pt-12 pb-16 sm:pt-20 sm:pb-24 md:pt-28 md:pb-32 lg:pt-36 lg:pb-40">
          <div className="lg:grid lg:grid-cols-12 lg:gap-12 items-center">
            {/* Left: Copy */}
            <div className="lg:col-span-7 text-center lg:text-left">
              <FadeUp>
                <h1
                  className="text-[2.25rem] leading-[1.1] sm:text-5xl lg:text-6xl font-bold tracking-tight"
                  style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }}
                >
                  <span className="text-white">{t('hero_headline')}</span>
                  <br />
                  <span style={{ color: '#C8FF00' }}>{t('hero_headline_accent')}</span>
                </h1>
              </FadeUp>
              <FadeUp delay={0.1}>
                <p className="mt-4 sm:mt-6 text-[0.9375rem] sm:text-lg text-white/60 leading-relaxed max-w-xl mx-auto lg:mx-0" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  {t('hero_subhead')}
                </p>
              </FadeUp>
              <FadeUp delay={0.2}>
                <div className="mt-6 sm:mt-8 flex flex-col gap-4 items-center lg:items-start">
                  <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                    <AppStoreButton />
                    <PlayStoreButton />
                  </div>
                  <a
                    href="#how-it-works"
                    data-testid="landing-hero-secondary-button"
                    className="inline-flex items-center gap-2 text-sm font-medium text-white/60 hover:text-white transition-colors duration-200"
                  >
                    {t('hero_cta_secondary')}
                    <ChevronRight size={16} />
                  </a>
                </div>
              </FadeUp>
            </div>

            {/* Right: App Preview */}
            <div className="lg:col-span-5 mt-10 sm:mt-14 lg:mt-0 flex justify-center">
              <FadeUp delay={0.3}>
                <div className="relative">
                  {/* Phone mockup frame */}
                  <div
                    className="relative rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden border-[2px] sm:border-[3px] border-white/15 w-[220px] sm:w-[260px] md:w-[280px]"
                    style={{
                      boxShadow: '0 25px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)',
                    }}
                  >
                    {/* Notch */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[90px] sm:w-[120px] h-[22px] sm:h-[28px] rounded-b-xl sm:rounded-b-2xl z-10" style={{ backgroundColor: '#030303' }} />
                    <img
                      src="https://customer-assets.emergentagent.com/job_anyelo-elite-1/artifacts/jl8mlbta_Untitled%20design%20%2839%29.jpg"
                      alt="Anyelo Elite App Preview"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                  {/* Floating stat card */}
                  <div className="absolute -bottom-3 sm:-bottom-4 left-0 sm:-left-4 px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border border-white/10" style={{ backgroundColor: '#0A0A0A', boxShadow: '0 10px 26px rgba(0,0,0,0.55)' }}>
                    <div className="text-lg sm:text-xl font-bold" style={{ color: '#C8FF00', fontFamily: 'Space Grotesk, sans-serif' }}>14+</div>
                    <div className="text-[0.65rem] sm:text-xs text-white/50">{t('bio_experience')}</div>
                  </div>
                  {/* Glow behind phone */}
                  <div className="absolute inset-0 -z-10 blur-3xl opacity-20" style={{ background: 'radial-gradient(circle at center, rgba(200,255,0,0.3), transparent 70%)' }} />
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section data-testid="landing-features" className="relative" style={{ backgroundColor: '#030303' }}>
        <div className="mx-auto max-w-[1120px] px-4 sm:px-6 lg:px-8 py-14 sm:py-20 md:py-24">
          <FadeUp>
            <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
                {t('features_title')}
              </h2>
              <p className="text-white/50 text-sm sm:text-base md:text-lg px-2" style={{ fontFamily: 'Figtree, sans-serif' }}>
                {t('features_subtitle')}
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
            {features.map((feat, i) => {
              const Icon = feat.icon;
              return (
                <FadeUp key={i} delay={i * 0.08}>
                  <div
                    className="group p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl border border-white/10 hover:border-white/20 transition-all duration-300"
                    style={{ backgroundColor: '#0A0A0A' }}
                    data-testid={`feature-card-${i}`}
                  >
                    <div
                      className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4"
                      style={{ backgroundColor: 'rgba(200,255,0,0.1)' }}
                    >
                      <Icon size={18} className="sm:hidden" style={{ color: '#C8FF00' }} />
                      <Icon size={20} className="hidden sm:block" style={{ color: '#C8FF00' }} />
                    </div>
                    <h3 className="text-white font-semibold text-[0.9375rem] sm:text-base mb-1.5 sm:mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {feat.title}
                    </h3>
                    <p className="text-white/50 text-[0.8125rem] sm:text-sm leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>
                      {feat.desc}
                    </p>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" data-testid="landing-how-it-works" className="relative" style={{ backgroundColor: '#030303' }}>
        <div className="mx-auto max-w-[1120px] px-4 sm:px-6 lg:px-8 py-14 sm:py-20 md:py-24">
          <FadeUp>
            <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
                {t('how_title')}
              </h2>
              <p className="text-white/50 text-sm sm:text-base md:text-lg px-2" style={{ fontFamily: 'Figtree, sans-serif' }}>
                {t('how_subtitle')}
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            {steps.map((step, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <div
                  className="p-5 sm:p-6 rounded-xl sm:rounded-2xl border border-white/10"
                  style={{ backgroundColor: '#0A0A0A' }}
                >
                  <div
                    className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4"
                    style={{ color: '#C8FF00', fontFamily: 'Space Grotesk, sans-serif', opacity: 0.6 }}
                  >
                    {step.num}
                  </div>
                  <h3 className="text-white font-semibold text-base sm:text-lg mb-1.5 sm:mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {step.title}
                  </h3>
                  <p className="text-white/50 text-[0.8125rem] sm:text-sm leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    {step.desc}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BIO / TRUST ===== */}
      <section data-testid="landing-bio" className="relative" style={{ backgroundColor: '#030303' }}>
        <div className="mx-auto max-w-[1120px] px-4 sm:px-6 lg:px-8 py-14 sm:py-20 md:py-24">
          <div className="lg:grid lg:grid-cols-12 lg:gap-12 items-center">
            {/* Image */}
            <div className="lg:col-span-5">
              <FadeUp>
                <div
                  className="rounded-xl sm:rounded-2xl overflow-hidden border border-white/10 max-w-[400px] mx-auto lg:max-w-none"
                  style={{ boxShadow: '0 18px 45px rgba(0,0,0,0.55)' }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_anyelo-elite-1/artifacts/lvs7d31s_633376190_1839664690074676_4512846829022822004_n.jpg"
                    alt="Anyelo - Coach"
                    className="w-full h-auto object-cover"
                    style={{ aspectRatio: '5/6' }}
                  />
                </div>
              </FadeUp>
            </div>

            {/* Text */}
            <div className="lg:col-span-7 mt-8 sm:mt-10 lg:mt-0">
              <FadeUp delay={0.1}>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
                  {t('bio_title')}
                </h2>
              </FadeUp>
              <FadeUp delay={0.15}>
                <p className="text-white/60 text-[0.9375rem] sm:text-base md:text-lg leading-relaxed mb-6 sm:mb-8" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  {t('bio_text')}
                </p>
              </FadeUp>
              <FadeUp delay={0.2}>
                <div className="flex flex-col gap-2.5 sm:gap-3 sm:flex-row">
                  {bioStats.map((stat, i) => {
                    const Icon = stat.icon;
                    return (
                      <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-lg sm:rounded-xl border border-white/10" style={{ backgroundColor: '#0A0A0A' }}>
                        <Icon size={18} style={{ color: '#C8FF00' }} className="shrink-0" />
                        <span className="text-white text-[0.8125rem] sm:text-sm font-medium">{stat.text}</span>
                      </div>
                    );
                  })}
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section id="download" data-testid="landing-final-cta" className="relative" style={{ backgroundColor: '#030303' }}>
        <div className="mx-auto max-w-[1120px] px-4 sm:px-6 lg:px-8 py-14 sm:py-20 md:py-24">
          <FadeUp>
            <div
              className="text-center p-6 sm:p-10 md:p-12 lg:p-16 rounded-xl sm:rounded-2xl border border-white/10 relative overflow-hidden"
              style={{ backgroundColor: '#0A0A0A' }}
            >
              {/* Subtle glow */}
              <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] sm:w-[400px] h-[150px] sm:h-[200px]" style={{ background: 'radial-gradient(50% 50% at 50% 0%, rgba(200,255,0,0.06), transparent 70%)' }} />
              </div>
              <div className="relative flex flex-col items-center">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
                  {t('final_cta_title')}
                </h2>
                <p className="text-white/50 text-sm sm:text-base md:text-lg mb-8 sm:mb-10 max-w-md mx-auto" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  {t('final_cta_text')}
                </p>

                {/* QR Code */}
                <SmartQRCode size={130} />

                {/* Store badges */}
                <div className="flex flex-col sm:flex-row gap-3 mt-6 w-full sm:w-auto">
                  <AppStoreButton size="lg" />
                  <PlayStoreButton size="lg" />
                </div>

                <p className="text-[0.7rem] text-white/25 mt-5 max-w-xs" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  {t('download_note')}
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
